import {
  useOutreach,
  useOutreachEmailPrompt,
  useOutreachEmailPromptDiff,
} from "@inrange/building-manager-api-client";
import CollapsibleCard from "../site/CollapsibleCard";
import OutreachTestPanel from "./OutreachTestPanel";
import PromptRuleSetEditor from "./PromptRuleSetEditor";

// The cold-outreach email writer's editable rule-set prompt. A thin task binding over
// the shared PromptRuleSetEditor - there is no per-role split (the recipient's role is
// a fact in each email's data, not a separate persona).
const OutreachEmailPromptCard = () => {
  const prompt = useOutreachEmailPrompt();
  const {
    saveOutreachPromptRuleMut,
    deleteOutreachPromptRuleMut,
    reorderOutreachPromptRulesMut,
    saveOutreachPromptPreambleMut,
    saveOutreachPromptAddendumMut,
    restoreOutreachPromptRevisionMut,
  } = useOutreach();

  const writing =
    saveOutreachPromptRuleMut.isLoading ||
    deleteOutreachPromptRuleMut.isLoading ||
    reorderOutreachPromptRulesMut.isLoading ||
    saveOutreachPromptPreambleMut.isLoading ||
    saveOutreachPromptAddendumMut.isLoading ||
    restoreOutreachPromptRevisionMut.isLoading;

  return (
    <CollapsibleCard title="Outreach email prompt" initialOpen={false}>
      <PromptRuleSetEditor
        prompt={prompt}
        writing={writing}
        saveRuleLoading={saveOutreachPromptRuleMut.isLoading}
        savePreambleLoading={saveOutreachPromptPreambleMut.isLoading}
        compiledPreviewLabel="Compiled prompt (what the writer sees)"
        loadFailedMessage="Could not load the outreach-email prompt."
        ruleCategoryPlaceholder="e.g. Structure, Personalisation, Truthfulness"
        description={
          <>
            The cold-outreach email writer&apos;s editable prompt is a{" "}
            <strong>preamble</strong> plus a set of addressable{" "}
            <strong>rules</strong> (each with why it exists, its limits and the
            evidence behind it). It drafts the emails offered from a lead&apos;s
            pulled contacts. Every change appends a new revision. The writer never
            invents facts - it only phrases the ones the &quot;Data used&quot; panel
            lists - and the fixed guardrail below (JSON shape, never fabricate, no em
            dashes, human-reviewed) is always appended and cannot be edited away.
          </>
        }
        emptyRulesHint={
          <>
            No rules on this revision - the whole prompt is in the preamble above.
            Use <strong>+ Add rule</strong> to break it out into addressable rules
            (each with its own why / limits / evidence), or{" "}
            <strong>Reset whole prompt to shipped default</strong> to adopt the
            shipped rule set.
          </>
        }
        onSaveRule={(a) => saveOutreachPromptRuleMut.mutateAsync(a)}
        onDeleteRule={(a) => deleteOutreachPromptRuleMut.mutateAsync(a)}
        onReorderRules={(a) => reorderOutreachPromptRulesMut.mutateAsync(a)}
        onSavePreamble={(a) => saveOutreachPromptPreambleMut.mutateAsync(a)}
        onResetToDefault={(a) => saveOutreachPromptAddendumMut.mutateAsync(a)}
        onRestoreRevision={(a) => restoreOutreachPromptRevisionMut.mutateAsync(a)}
        useDiff={useOutreachEmailPromptDiff}
        renderTestPanel={(addendum) => <OutreachTestPanel addendum={addendum} />}
      />
    </CollapsibleCard>
  );
};

export default OutreachEmailPromptCard;
